import { useCallback, useEffect, useRef, useState } from 'react';
import { ColumnWidth } from '../../../enum';
import { UserDetails } from '../../../model/types';
import { TextField } from '../../Fields/View/TextField';
import { ColumnDefinition, TableColumnRenderer } from '../DataTable';
import { Header, SortDirection, Sortobj } from '../header';
import { AddColumn } from '../header/column/AddColumn';
import FieldMapper from '../Mapper';

const isDesktop = () => {
	const userAgent = navigator.userAgent.toLowerCase();
	return !userAgent.match(/mobile|tablet|phone/) || window.innerWidth >= 768;
};

const shouldFixed = (index: number): 'left' | 'right' | undefined =>
	isDesktop() ? (index === 0 ? 'left' : undefined) : undefined;

const useGenerateColumns = (
	filteredColumn: ColumnDefinition[],
	sort: Sortobj | undefined,
	canAddColumn: boolean,
	getUsers: (ids: string[]) => Promise<UserDetails[]>,
	isTile = false,
	customColumns?: TableColumnRenderer[],
	timeZoneOffsetMins?: number,
	roleMapper?: Record<string, string>
) => {
	const [columns, setColumns] = useState<TableColumnRenderer[]>([]);
	const columnWidthsRef = useRef<Record<string, number>>({});
	let totalColumnWidth = columns.reduce((total, col) => {
		return total + (col.width ? col.width : ColumnWidth.COLUMN_DEFAULT);
	}, 0);
	const handleResize = useCallback(
		(
				key: string,
				index: number,
				// eslint-disable-next-line @typescript-eslint/no-shadow
				columnWidthsRef: React.MutableRefObject<Record<string, number>>
			) =>
			(
				_: React.SyntheticEvent<Element>,
				{ size: { width } }: { size: { width: number } }
			) => {
				if (width < ColumnWidth.MIN_WIDTH || width > ColumnWidth.MAX_WIDTH) {
					return;
				}
				setColumns(prevColumns => {
					const nextColumns = [...prevColumns];
					// eslint-disable-next-line react-hooks/exhaustive-deps
					totalColumnWidth += prevColumns[index].width - width;
					nextColumns[index] = { ...nextColumns[index], width };
					return nextColumns;
				});
				columnWidthsRef.current[key] = width;
			},

		[]
	);
	const generateColumns = (
		columnDefinitions: ColumnDefinition[],
		sortObj: Sortobj | undefined
	) => {
		// Function to get the ARIA sort attribute
		const getHeaderCellProps = (columnName: string) => ({
			'aria-sort':
				sortObj?.sortKey === columnName
					? sortObj.direction === SortDirection.DESC
						? 'descending'
						: 'ascending'
					: 'none',
		});
		// Generate column configuration
		const generatedColumns: TableColumnRenderer[] = columnDefinitions.map(
			(column: ColumnDefinition, index: number) => {
				// Component to render cell content
				const CellComponent = FieldMapper.table?.view?.[column.component] ?? TextField;
				const { name, editable } = column;
				const baseColumn = {
					title: isTile ? (
						column.label
					) : (
						<Header
							column={column}
							key={name}
							sort={sortObj}
							columnIndex={index}
							totalColumns={columnDefinitions.length}
						/>
					),
					dataIndex: name,
					key: name,
					editable: editable ?? true,
					render: (cell: any, record: any) => {
						const modifiedRecord = { ...record, recordId: record?._id };
						const columnProps = {
							...column,
							roleMapper,
							timeZoneOffsetMins,
							getUsers,
							dateFormat: column?.attributes?.format,
						};
						return (
							<CellComponent
								{...columnProps}
								fieldValue={cell}
								key={index}
								{...modifiedRecord}
							/>
						);
					},
					onHeaderCell: ({ width }: { width: number }) => ({
						...getHeaderCellProps(name),
						width,
						onResize: handleResize(name, index, columnWidthsRef),
					}),
					onCell: (record: any) => ({
						dataIndex: name,
						record,
						column,
						editable: editable ?? true,
						colKey: name,
						colIndex: index,
						timeZoneOffsetMins,
					}),
					shouldCellUpdate: (record: any, prevRecord: any) =>
						record[name] !== prevRecord[name],
				};

				if (isTile) {
					return {
						...baseColumn,
						width: index === 0 ? ColumnWidth.TITLE_WIDGET : ColumnWidth.COLUMN_WIDGET,
						sorter: column.sortable ?? false,
					};
				} else {
					const width =
						columnWidthsRef.current[name] ??
						(index === 0 ? ColumnWidth.TITLE_DEFAULT : ColumnWidth.COLUMN_DEFAULT);
					return { ...baseColumn, width, fixed: shouldFixed(index) };
				}
			}
		);

		if (customColumns) {
			generatedColumns.push(...customColumns);
		}

		if (canAddColumn) {
			generatedColumns.push({
				title: <AddColumn />,
				dataIndex: 'add-column',
				editable: false,
				width: ColumnWidth.COLUMN_DEFAULT,
				key: 'add-column',
			});

			// moving width=0 condition inside add column as payment solution doesn't have add column functionality
			const tableWidth = document
				.querySelector('.ant-table-wrapper')
				?.getBoundingClientRect().width;
			const maxColumns = (tableWidth ?? window.innerWidth) / ColumnWidth.MIN_WIDTH;
			const columnLengths = generatedColumns.length;
			if (columnLengths < maxColumns) {
				generatedColumns[columnLengths - 1].width = 0;
			}
		}
		return generatedColumns;
	};

	const columnsData = useCallback(() => {
		const generatedColumns = generateColumns(filteredColumn, sort);
		setColumns(generatedColumns);
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filteredColumn, sort]);

	useEffect(() => {
		columnsData();
	}, [columnsData]);

	return { columns, totalColumnWidth };
};

export default useGenerateColumns;
